// Wires the secure pieces into one system: the PluginHost (runtime), the
// PermissionBroker (deny-by-default + consent), and host effects (UI, file
// reads). The broker is declared from each manifest on install, every brokered
// call is capability-checked, and grant/revoke is forwarded to the running
// plugin. The host app supplies the concrete store, consent UI, file reader,
// and UI callbacks.

import type { MessageOptions, QuickPickItem, PermissionCapability } from '@slate/plugin-api';
import { PluginHost, PluginRefusedError, type LoadGuard } from './PluginHost';
import { PermissionBroker, type ConsentPrompt, type GrantStore } from './permissionBroker';
import { PluginConnection, type PluginHostServices } from './rpcHost';
import { HostFoldRegistry } from '../folds/hostFoldRegistry';
import { inProcessRuntimeFactory, type InstalledPlugin, type PluginRuntimeFactory } from './runtime';
import { createKillSwitch, type KillSwitch } from './integrity/killSwitch';
import { BUNDLED_REVOCATION_LIST } from './integrity/revocationList';
import { defaultVerifier, type PluginVerifier } from './integrity/verifier';

export interface PluginSystemUi {
  showMessage(message: string, options?: MessageOptions): Promise<string | undefined>;
  showQuickPick(
    items: QuickPickItem[],
    options?: { placeholder?: string },
  ): Promise<QuickPickItem | undefined>;
  onCommandRegistered?(pluginId: string, commandId: string): void;
  onCommandUnregistered?(pluginId: string, commandId: string): void;
  onViewRegistered?(pluginId: string, viewId: string): void;
  onViewUnregistered?(pluginId: string, viewId: string): void;
  onSurfaceRegistered?(pluginId: string, surfaceId: string): void;
  onSurfaceUnregistered?(pluginId: string, surfaceId: string): void;
  onSurfaceViewRegistered?(pluginId: string, viewId: string): void;
  onSurfaceViewUnregistered?(pluginId: string, viewId: string): void;
  showModal?(pluginId: string, surfaceId: string): Promise<void>;
  executeCommand?(id: string, args: unknown[]): Promise<unknown>;
}

export interface PluginSystemDeps {
  /** Defaults to the trusted in-process tier. Third-party plugins pass the
   *  sealed-worker factory. */
  factory?: PluginRuntimeFactory;
  store: GrantStore;
  consent: ConsentPrompt;
  /** Scope-enforced file reader supplied by the host (e.g. workspace-limited). */
  fsRead(path: string): Promise<string>;
  /** The active editor's text (not gated). Defaults to undefined (no editor). */
  getActiveText?(): string | undefined | Promise<string | undefined>;
  ui: PluginSystemUi;
  /** Injectable for tests. Defaults to the bundled static revocation list. */
  killSwitch?: KillSwitch;
  /** Injectable for tests. Defaults to the module-admitting fail-closed verifier. */
  verify?: PluginVerifier;
}

export interface PluginSystem {
  host: PluginHost;
  broker: PermissionBroker;
  /** Validate + install, declaring the manifest's permissions on the broker. */
  install(plugin: InstalledPlugin): void;
}

const noop = (): void => undefined;

export function createPluginSystem(deps: PluginSystemDeps): PluginSystem {
  const broker = new PermissionBroker(deps.store, deps.consent);
  const reasons = new Map<string, Map<string, string>>();

  const reasonFor = (pluginId: string, capability: PermissionCapability): string =>
    reasons.get(pluginId)?.get(capability) ?? `Use ${capability}`;

  let host: PluginHost;
  // Bridges plugin fold providers into the core fold registry. The bridge fetches
  // ranges back through the host connection; the host drops a plugin's bridges on
  // disable/uninstall (host.setFoldSink below).
  const foldBridge = new HostFoldRegistry();
  const services: PluginHostServices = {
    onCommandRegistered: deps.ui.onCommandRegistered ?? noop,
    onCommandUnregistered: deps.ui.onCommandUnregistered ?? noop,
    onViewRegistered: deps.ui.onViewRegistered ?? noop,
    onViewUnregistered: deps.ui.onViewUnregistered ?? noop,
    onSurfaceRegistered: deps.ui.onSurfaceRegistered ?? noop,
    onSurfaceUnregistered: deps.ui.onSurfaceUnregistered ?? noop,
    onSurfaceViewRegistered: deps.ui.onSurfaceViewRegistered ?? noop,
    onSurfaceViewUnregistered: deps.ui.onSurfaceViewUnregistered ?? noop,
    onSurfaceMessage: (pluginId, surfaceId, message) =>
      host.emitSurfaceMessage(pluginId, surfaceId, message),
    showModal: deps.ui.showModal ?? (async () => undefined),
    showMessage: deps.ui.showMessage,
    showQuickPick: deps.ui.showQuickPick,
    executeCommand: deps.ui.executeCommand ?? (async () => undefined),
    permissionQuery: (pluginId, capability) => broker.query(pluginId, capability),
    permissionRequest: (pluginId, capability) =>
      broker.request(pluginId, capability, reasonFor(pluginId, capability)),
    // Every brokered file read is gated on a live grant; the path scope is
    // enforced by the host-supplied reader.
    fsRead: async (pluginId, path) => {
      if (!broker.check(pluginId, 'fs.read')) {
        throw new Error(`fs.read not granted for "${pluginId}"`);
      }
      return deps.fsRead(path);
    },
    getActiveText: deps.getActiveText ?? (() => undefined),
    // A plugin registered/unregistered a fold provider: bridge it into the core
    // fold registry (or drop it). The bridge fetches ranges through the host.
    onFoldProviderRegistered: (pluginId, providerId, extensions) =>
      foldBridge.add(pluginId, providerId, extensions, {
        getFolds: (pid, source, fileName) => host.getFolds(pluginId, pid, source, fileName),
      }),
    onFoldProviderUnregistered: (pluginId, providerId) =>
      foldBridge.removeProvider(pluginId, providerId),
  };

  // Integrity gates (fail-closed). The kill switch is checked synchronously in
  // PluginHost.install (so a revoked plugin registers no contributions); the
  // signature verifier runs as the loadGuard at activate, before any worker
  // spawns. In-process/first-party modules skip signing (spec 4.1) because the
  // verifier short-circuits on `plugin.module`.
  const killSwitch = deps.killSwitch ?? createKillSwitch(BUNDLED_REVOCATION_LIST);
  const verifier = deps.verify ?? defaultVerifier;
  const loadGuard: LoadGuard = async (plugin) => {
    const res = await verifier.verify(plugin);
    if (!res.ok) {
      throw new PluginRefusedError(
        `plugin "${plugin.manifest.id}" failed integrity: ${res.reason}`,
      );
    }
  };

  host = new PluginHost(deps.factory ?? inProcessRuntimeFactory(), services, {
    killSwitch,
    loadGuard,
  });
  // The host drops a plugin's bridged fold providers on disable/uninstall.
  host.setFoldSink(foldBridge);

  // Live reactivity: a grant or revocation reaches the running plugin.
  broker.onDidChange((pluginId, capability, state) =>
    host.notifyPermissionChanged(pluginId, capability, state),
  );

  return {
    host,
    broker,
    install(plugin) {
      host.install(plugin);
      const declared = plugin.manifest.permissions ?? [];
      broker.declare(
        plugin.manifest.id,
        declared.map((p) => p.capability),
      );
      const map = new Map<string, string>();
      for (const p of declared) map.set(p.capability, p.reason);
      reasons.set(plugin.manifest.id, map);
    },
  };
}

// Re-exported so a single import wires the host UI.
export { PluginConnection };
